import React, { useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { connect } from 'react-redux';
import { Formik, Field, ErrorMessage, FormikProps } from 'formik';
import * as Yup from 'yup';

import * as Bixby from '$/core';
import { AppState } from '$/store';
import Modal from '$/components/global/modal';
import InputBlock from '$/components/global/inputBlock';
import BixButton from '$/components/global/bixButton';
import BixCheckbox from '$/components/global/bixCheckbox';
import { addClientCompanyTask } from '$/store/company/actions';
import PeopleSearchBox from '$/components/global/peopleSearchBox';

import './CreateClientCompanyTask.scss';
import { DeclarationType } from '../../../../core/enums';

export interface ICreateClientCompanyTaskProps {
  clientCompanyTaskType?: Bixby.Enums.ClientCompanyTaskType;
  currentClient?: string;
  isAddingClientCompanyTask?: boolean;
  currentCompany?: Bixby.Models.AdminCompany;
  dataSiteProviders?: Bixby.Models.IIdentifiable[];
  dataSiteTypes?: Bixby.Models.IIdentifiable[];
  onCloseModal: () => void;
  dispatchAddClientCompanyTask: (newCompany: Bixby.Models.CreateClientCompanyTask) => Promise<void>;
}

export interface CreateClientCompanyTaskValues {
  dataSiteFromEmail: string;
  timeFromEmail: string;
  client: string;
  dataSiteSource: string;
  companyName: string;
  bixbyId: string;
  dataSiteProvider: string;
  dataSiteType: string;
  clientAccess: string;
  totalClientEmails: number;
  isEmailOnly: boolean;
  assignerDocs: CreateClientCompanyTaskAssignerDocValues[];
  hasPrivateDeclaration: boolean;
}

export interface CreateClientCompanyTaskAssignerDocValues {
  client: string;
  emails: number | undefined;
  docs: string;
  fileSizes: string;
}

const CreateClientCompanyTask: React.FC<ICreateClientCompanyTaskProps> = props => {
  const {
    clientCompanyTaskType,
    currentClient,
    isAddingClientCompanyTask,
    currentCompany,
    dataSiteProviders,
    dataSiteTypes,
    onCloseModal,
    dispatchAddClientCompanyTask,
  } = props;

  const [showAssignerFormValidationError, setShowAssignerFormValidationError] = useState<boolean>(false);
  const [assignees, setAssignees] = useState<string[]>([]);

  const isMissingEmail = clientCompanyTaskType === Bixby.Enums.ClientCompanyTaskType.MissingEmail;
  const isEmail = clientCompanyTaskType === Bixby.Enums.ClientCompanyTaskType.Email;
  const isRogue = clientCompanyTaskType === Bixby.Enums.ClientCompanyTaskType.Rogue;

  const dataSiteProvider: string = (
    (dataSiteProviders || []).find(x => x.id == currentCompany?.dataSiteProvider_Id) ||
    ({} as Bixby.Models.IIdentifiable)
  ).name;

  const dataSiteName = currentCompany?.dataSiteName || '';

  const initialNewTask: CreateClientCompanyTaskValues = {
    dataSiteFromEmail: isEmail ? dataSiteName : '',
    timeFromEmail: dayjs(new Date()).format('ddd MM/DD/YYYY h:mm a').toString(),
    client: currentClient || '',
    dataSiteSource: dataSiteName,
    companyName: currentCompany?.name ?? '',
    bixbyId: currentCompany?.bixbyId ?? '',
    dataSiteProvider: dataSiteProvider,
    dataSiteType: (
      (dataSiteTypes || []).find(x => x.id == currentCompany?.dataSiteType_Id) || ({} as Bixby.Models.IIdentifiable)
    ).name,
    clientAccess: `CLIENT ACCESS (TOTAL: ${currentCompany?.clients.length})`,
    totalClientEmails: 0,
    isEmailOnly: false,
    assignerDocs:
      currentCompany?.clients
        ?.filter(c => c.basisOfAccess !== Bixby.Enums.BasisOfAccess.CloHoldingsAccess)
        ?.map(c => {
          return {
            client: c.client_Name,
            emails: undefined,
          } as CreateClientCompanyTaskAssignerDocValues;
        })
        ?.sort((a, b) => (a.client > b.client ? 1 : -1)) ?? [],
    hasPrivateDeclaration: currentCompany?.declarationType !== Bixby.Enums.DeclarationType.Public,
  };

  const formikRef = useRef<FormikProps<CreateClientCompanyTaskValues>>(null);

  const handleCloseModal = () => {
    if (formikRef && formikRef.current) {
      formikRef.current.resetForm();
    }

    setAssignees([]);

    onCloseModal();
  };

  const createAssignerDocs: (values: CreateClientCompanyTaskValues, setFieldValue: any) => JSX.Element[] | undefined = (
    values: CreateClientCompanyTaskValues,
    setFieldValue: any
  ) => {
    const assignerDocs: JSX.Element[] = [];
    for (let i = 0; i < initialNewTask.assignerDocs.length; i++) {
      const assigner = initialNewTask.assignerDocs[i];
      assignerDocs.push(
        <div key={`${i}-client`} className="profile-input-column">
          <div className="search_block">
            <div className="search_block_title">{assigner.client}</div>
          </div>
        </div>
      );

      if (isEmail) {
        assignerDocs.push(
          <div key={`${i}-emails`} className="profile-input-column">
            <div className="search_block">
              <Field
                name={`assignerDocs.${i}.emails`}
                render={({ field }: { [key: string]: any }) => (
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={Number(field.value).toString()}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      let parsedValue: number | undefined = parseInt(e.target.value);
                      if (isNaN(parsedValue) || parsedValue <= 0) {
                        parsedValue = undefined;
                      }

                      setFieldValue(`assignerDocs.${i}.emails`, parsedValue);
                      setFieldValue(
                        'totalClientEmails',
                        Bixby.Utils.sum(
                          values.assignerDocs.filter(x => x.client !== assigner.client),
                          'emails'
                        ) + (parsedValue || 0)
                      );
                    }}
                  />
                )}
              />
            </div>
            <ErrorMessage key={`${i}-emails-error`} name={`assignerDocs.${i}.emails`}>
              {msg => <div className="error error-message">{msg}</div>}
            </ErrorMessage>
          </div>
        );
      }

      assignerDocs.push(
        <div key={`${i}-docs`} className="profile-input-column">
          <div className="search_block">
            <Field name={`assignerDocs.${i}.docs`} />
          </div>
        </div>
      );

      if (isRogue || isMissingEmail) {
        assignerDocs.push(
          <div key={`${i}-fileSizes`} className="profile-input-column">
            <div className="search_block">
              <Field name={`assignerDocs.${i}.fileSizes`} />
            </div>
          </div>
        );
      }
    }

    return assignerDocs;
  };

  let schema = Yup.object({});
  if (isEmail) {
    schema = Yup.object({
      assignerDocs: Yup.array().of(
        Yup.object().shape({
          emails: Yup.number().required('Client email count required.'),
        })
      ),
    });
  }

  const validateAssignerEmails: (values: CreateClientCompanyTaskValues) => boolean = (
    values: CreateClientCompanyTaskValues
  ) => {
    const result = values.assignerDocs.every((e, i, a) => {
      const parsedValue: number | undefined = parseInt(Number(e.emails).toString());
      if (isNaN(parsedValue) || parsedValue <= 0) {
        return false;
      }
      if (i === 0) {
        return true;
      }

      const currentValue: number | undefined = parseInt(Number(e.emails).toString());
      const priorValue: number | undefined = parseInt(Number(a[i - 1].emails).toString());

      return currentValue === priorValue;
    });
    return result;
  };

  const title: string = useMemo(() => {
    if (clientCompanyTaskType === Bixby.Enums.ClientCompanyTaskType.Email) {
      return 'Assign Email Task:';
    }

    if (clientCompanyTaskType === Bixby.Enums.ClientCompanyTaskType.MissingEmail) {
      return 'Assign Missing Email Task:';
    }

    return 'Assign Rogue Task:';
  }, [clientCompanyTaskType]);

  let subtitle = `${currentClient} | ${dataSiteProvider} | ${dataSiteName}`;
  if (isRogue) {
    subtitle = 'ROGUE POST | ' + subtitle;
  }

  const createTaskForm = (
    <Modal
      title={title}
      subtitle={subtitle}
      forForm
      onClose={handleCloseModal}
      active={isMissingEmail || isEmail || isRogue}>
      <div>
        <Formik
          innerRef={formikRef}
          initialValues={initialNewTask}
          validationSchema={schema}
          onSubmit={async (values: CreateClientCompanyTaskValues) => {
            if (!clientCompanyTaskType || !currentCompany) {
              return;
            }
            const newTask: Bixby.Models.CreateClientCompanyTask = {
              ...values,
              taskType: clientCompanyTaskType,
              assignees: assignees,
              companyId: currentCompany.id,
            };

            if (isEmail && validateAssignerEmails(values) === false) {
              setShowAssignerFormValidationError(true);
              return;
            } else {
              setShowAssignerFormValidationError(false);
              await dispatchAddClientCompanyTask(newTask);
              handleCloseModal();
            }
          }}
          render={({ handleSubmit, setFieldValue, values }) => (
            <form className="create-task-form" onSubmit={handleSubmit}>
              <div className="profile-input-container">
                <div>
                  {isEmail && (
                    <InputBlock title="Data Site (from Email)">
                      <Field type="text" name="dataSiteFromEmail" />
                    </InputBlock>
                  )}
                  {isEmail && (
                    <InputBlock title="Time (from Email)">
                      <Field type="text" name="timeFromEmail" />
                    </InputBlock>
                  )}
                  <InputBlock title="Client">
                    <input type="text" value={initialNewTask.client} disabled />
                  </InputBlock>
                  <InputBlock title="Data Site Source">
                    {isEmail && <input type="text" value={initialNewTask.dataSiteSource} disabled />}
                    {(isRogue || isMissingEmail) && <Field type="text" name="dataSiteSource" />}
                  </InputBlock>
                  <InputBlock title="Company Name">
                    <input type="text" value={initialNewTask.companyName} disabled />
                  </InputBlock>
                  <InputBlock title="BixbyID">
                    <input type="text" value={initialNewTask.bixbyId} disabled />
                  </InputBlock>
                  <InputBlock title="Data Site Provider">
                    <input type="text" value={initialNewTask.dataSiteProvider} disabled />
                  </InputBlock>
                  <InputBlock title="Data Site Type">
                    <input type="text" value={initialNewTask.dataSiteType} disabled />
                  </InputBlock>
                  <InputBlock title="Client Access">
                    <input type="text" value={initialNewTask.clientAccess} disabled />
                  </InputBlock>
                  {isEmail && (
                    <InputBlock title="Total Client Emails">
                      <Field type="number" name="totalClientEmails" disabled />
                    </InputBlock>
                  )}
                  {isEmail && (
                    <div className="email-only">
                      <Field
                        name="isEmailOnly"
                        render={({ field }: { [key: string]: any }) => (
                          <BixCheckbox
                            id="isEmailOnly"
                            checked={field.value}
                            title="Email Only?"
                            onChange={field.onChange(field.name)}>
                            Email Only?
                          </BixCheckbox>
                        )}
                      />
                    </div>
                  )}
                  <hr />

                  <div className="search_block">
                    <div className="search_block_title">Assigner Email / Doc Count</div>
                  </div>
                  <div className="profile-input-container">
                    <div className="profile-input-column">
                      <div className="search_block">
                        <div className="search_block_title header">Client</div>
                      </div>
                    </div>
                    {isEmail && (
                      <div className="profile-input-column">
                        <div className="search_block">
                          <div className="search_block_title header">Emails</div>
                        </div>
                      </div>
                    )}
                    <div className="profile-input-column">
                      <div className="search_block">
                        <div className="search_block_title header">Docs</div>
                      </div>
                    </div>
                    {(isRogue || isMissingEmail) && (
                      <div className="profile-input-column">
                        <div className="search_block">
                          <div className="search_block_title header">File Sizes</div>
                        </div>
                      </div>
                    )}

                    {createAssignerDocs(values, setFieldValue)}
                  </div>
                  <hr />
                  <PeopleSearchBox
                    title="Assign Task"
                    showMnpiUsers={currentCompany?.declarationType === DeclarationType.Private}
                    onChange={(ids: string[]) => setAssignees(ids)}
                  />
                </div>
              </div>
              {isEmail && showAssignerFormValidationError && (
                <div className="error error-message">Client email counts must match.</div>
              )}
              <BixButton
                useSmall
                type="submit"
                disabled={isAddingClientCompanyTask}
                useLoading
                isLoading={isAddingClientCompanyTask}>
                Allocate Task
              </BixButton>
            </form>
          )}></Formik>
      </div>
    </Modal>
  );

  if (!currentCompany || !currentClient || !clientCompanyTaskType) {
    return null;
  }

  return <div>{createTaskForm}</div>;
};

const mapStateToProps = (state: AppState) => {
  return {
    companyIsLoading: state.companyState.isLoading,
    error: state.companyState.error,
    currentCompany: state.companyState.currentCompany,
    isAddingClientCompanyTask: state.companyState.isAddingClientCompanyTask,
    dataSiteProviders: state.dataSiteProviderState.data,
    dataSiteTypes: state.dataSiteTypeState.data,
    hasPrivateDeclaration: state.companyState.currentCompany?.declarationType !== Bixby.Enums.DeclarationType.Public,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchAddClientCompanyTask: (model: Bixby.Models.CreateClientCompanyTask) => dispatch(addClientCompanyTask(model)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateClientCompanyTask);
